// KeyDisplay.jsx - Display API keys and request status
import React, { useState } from 'react';
import { useDeveloperAuth } from '../../contexts/DeveloperAuthContext';

function KeyDisplay({ keys, requests, onRefresh }) {
  const { authFetch } = useDeveloperAuth();
  const [copiedKey, setCopiedKey] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [revealingKeyId, setRevealingKeyId] = useState(null);
  const [revealErrorByKeyId, setRevealErrorByKeyId] = useState({});

  const copyToClipboard = async (text, keyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      setRevealErrorByKeyId((prev) => ({
        ...prev,
        [keyId]: 'Copy failed. Please select the key and copy manually.'
      }));
    }
  };

  const revealKey = async (keyId) => {
    setRevealingKeyId(keyId);
    setRevealErrorByKeyId((prev) => ({ ...prev, [keyId]: null }));
    try {
      const response = await authFetch('/api/developer/reveal-key', {
        method: 'POST',
        body: JSON.stringify({ keyId })
      });
      const data = await response.json();
      if (!response.ok) {
        setRevealErrorByKeyId((prev) => ({ ...prev, [keyId]: data.message || 'Failed to reveal key' }));
        return;
      }

      setRevealedKeys((prev) => ({ ...prev, [keyId]: data.apiKey }));
      if (onRefresh) onRefresh();
    } catch (error) {
      setRevealErrorByKeyId((prev) => ({ ...prev, [keyId]: error.message || 'Failed to reveal key' }));
    } finally {
      setRevealingKeyId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Separate pending and processed requests
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-8">
      {/* Active API Keys */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Your API Keys</h3>
          <button
            onClick={onRefresh}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Refresh
          </button>
        </div>

        {keys.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <p className="text-gray-600">No API keys yet</p>
            <p className="text-sm text-gray-500 mt-1">Submit a request to get API access</p>
          </div>
        ) : (
          <div className="space-y-4">
            {keys.map((key) => (
              <div
                key={key.id}
                className="bg-white rounded-2xl border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900">{key.key_name}</h4>
                    <p className="text-sm text-gray-500">Created {formatDate(key.created_at)}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    key.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {key.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Key Prefix */}
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono text-gray-700">{key.key_prefix}</code>
                    <button
                      onClick={() => copyToClipboard(key.key_prefix, key.id)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      {copiedKey === key.id ? 'Copied!' : 'Copy prefix'}
                    </button>
                  </div>
                </div>

                {/* One-time raw key reveal */}
                <div className="mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-blue-700">
                          View your raw API key (login required). Keep it secure.
                        </p>
                        {key.developer_key_revealed_at && (
                          <p className="text-[11px] text-blue-700/80 mt-1">
                            First revealed on {formatDate(key.developer_key_revealed_at)}.
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => revealKey(key.id)}
                        disabled={revealingKeyId === key.id}
                        className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                      >
                        {revealingKeyId === key.id ? 'Loading...' : 'View Key'}
                      </button>
                    </div>

                    {revealedKeys[key.id] && (
                      <div className="mt-3 bg-white/60 border border-blue-200 rounded-lg p-3">
                        <p className="text-[11px] text-blue-800 font-medium mb-2">Raw API Key</p>
                        <div className="flex items-start justify-between gap-3">
                          <code className="block text-sm font-mono text-blue-950 break-all select-all">
                            {revealedKeys[key.id]}
                          </code>
                          <button
                            onClick={() => copyToClipboard(revealedKeys[key.id], `raw-${key.id}`)}
                            className="shrink-0 text-xs text-blue-700 hover:text-blue-900"
                          >
                            {copiedKey === `raw-${key.id}` ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {revealErrorByKeyId[key.id] && (
                    <p className="text-xs text-red-600 mt-2">{revealErrorByKeyId[key.id]}</p>
                  )}
                </div>

                {/* Usage Stats */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Total Usage</p>
                    <p className="font-medium text-gray-900">{key.total_usage?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">This Month</p>
                    <p className="font-medium text-gray-900">
                      {key.current_month_usage?.toLocaleString() || 0}
                      {key.monthly_limit && (
                        <span className="text-gray-400"> / {key.monthly_limit.toLocaleString()}</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Used</p>
                    <p className="font-medium text-gray-900">
                      {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                    </p>
                  </div>
                </div>

                {/* Rate Limits */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Rate limits: {key.rate_limit_per_minute}/min, {key.rate_limit_per_day}/day
                    {key.expires_at && ` | Expires: ${formatDate(key.expires_at)}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Requests</h3>
          <div className="space-y-4">
            {pendingRequests.map((request) => (
              <div
                key={request.id}
                className="bg-amber-50 rounded-2xl border border-amber-200 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-amber-800">Submitted {formatDate(request.created_at)}</p>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
                <p className="text-gray-700 text-sm">{request.purpose}</p>
                <div className="mt-4 p-3 bg-amber-100/50 rounded-lg">
                  <p className="text-xs text-amber-700">
                    Your request is being reviewed. You'll receive an email once it's processed.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Request History */}
      {processedRequests.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Request History</h3>
          <div className="space-y-4">
            {processedRequests.map((request) => (
              <div
                key={request.id}
                className={`rounded-2xl border p-6 ${
                  request.status === 'approved'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm text-gray-600">
                    {request.status === 'approved' ? 'Approved' : 'Rejected'} on {formatDate(request.reviewed_at)}
                  </p>
                  {getStatusBadge(request.status)}
                </div>
                <p className="text-gray-700 text-sm mb-2">{request.purpose}</p>
                {request.status === 'rejected' && request.rejection_reason && (
                  <div className="mt-3 p-3 bg-red-100/50 rounded-lg">
                    <p className="text-xs text-red-700">
                      <strong>Reason:</strong> {request.rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default KeyDisplay;
